import { FC, useContext, useEffect, useMemo } from 'react';
import { createGQLDataContext } from 'utils/createDataContext';
import { useGQLQuery } from 'hooks';
import {
  NotificationPreferenceControlEnum,
  OrganizationSettingsPageGeneralSettingsQuery,
  OrganizationSettingsPageGeneralSettingsQueryVariables,
  TimeFormatEnum,
} from 'types/graphql';
import { GQLAPIContextType } from 'types/common.types';
import { getCurrentlySelectedLocation } from 'utils/getCurrentlySelectedLocation';
import { UseGQLQueryConfig } from 'hooks/useGQLQuery/useGQLQuery.types';
import { QUERY_organizationSettingsPageGeneralSettings } from 'queries';

type GeneralSettingsQueryData = OrganizationSettingsPageGeneralSettingsQuery & {
  fetchCommunity?: {
    organization?: {
      timeFormatConfiguration?: TimeFormatEnum;
      timeFormat?: TimeFormatEnum;
      licenceNumber?: string | null;
      smsNotificationsEnabled?: boolean;
      emailShiftNotificationsEnabled?: boolean;
      notificationPreferenceControl?: NotificationPreferenceControlEnum;
      smsPricingConfirmedAt?: string | null;
    };
  };
};

export const GeneralSettingsDataContext =
  createGQLDataContext<GQLAPIContextType<GeneralSettingsQueryData>>();

export const GeneralSettingsDataProvider: FC = ({ children }) => {
  const locationId = getCurrentlySelectedLocation();

  const generalSettingsConfigMemo: UseGQLQueryConfig<
    GeneralSettingsQueryData,
    GeneralSettingsQueryData,
    OrganizationSettingsPageGeneralSettingsQueryVariables
  > = useMemo(
    () => ({
      key: 'FETCH_GENERAL_SETTINGS_PAGE',
      query: QUERY_organizationSettingsPageGeneralSettings,
      variables: {
        locationId: Number(locationId?.value),
      },
    }),
    [locationId],
  );

  const { data, fetch, ...restQuery } =
    useGQLQuery<
      GeneralSettingsQueryData,
      GeneralSettingsQueryData,
      OrganizationSettingsPageGeneralSettingsQueryVariables
    >(generalSettingsConfigMemo);

  useEffect(() => {
    if (!restQuery?.called && locationId) fetch();
  }, [locationId]); // eslint-disable-line

  const contextValues = {
    data: {
      fetchCommunity: {
        organization: {
          timeFormatConfiguration:
            (data as GeneralSettingsQueryData)?.fetchCommunity?.organization
              ?.timeFormatConfiguration || TimeFormatEnum.standard,
          timeFormat:
            (data as GeneralSettingsQueryData)?.fetchCommunity?.organization?.timeFormat ||
            TimeFormatEnum.standard,
          updatedAt: data?.fetchCommunity?.organization?.updatedAt,
          createdAt: data?.fetchCommunity?.organization?.createdAt,
          relayId: data?.fetchCommunity?.organization?.relayId,
          lastEditedBy: data?.fetchCommunity?.organization?.lastEditedBy,
          licenceNumber: (data as GeneralSettingsQueryData)?.fetchCommunity?.organization
            ?.licenceNumber,
          smsNotificationsEnabled:
            (data as GeneralSettingsQueryData)?.fetchCommunity?.organization
              ?.smsNotificationsEnabled ?? false,
          emailShiftNotificationsEnabled:
            (data as GeneralSettingsQueryData)?.fetchCommunity?.organization
              ?.emailShiftNotificationsEnabled ?? false,
          notificationPreferenceControl:
            (data as GeneralSettingsQueryData)?.fetchCommunity?.organization
              ?.notificationPreferenceControl ?? NotificationPreferenceControlEnum.SCHEDULER_ONLY,
          smsPricingConfirmedAt:
            (data as GeneralSettingsQueryData)?.fetchCommunity?.organization
              ?.smsPricingConfirmedAt ?? null,
        },
      },
    },
    fetch,
    ...restQuery,
  };
  return (
    <GeneralSettingsDataContext.Provider value={contextValues}>
      {children}
    </GeneralSettingsDataContext.Provider>
  );
};

export const useGeneralSettingsDataContext = (): GQLAPIContextType<GeneralSettingsQueryData> =>
  useContext(GeneralSettingsDataContext);
